import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { isDefined } from '../../../../../utils/variables';
import { AssetManager } from '../../../../../viewer/asset-manager';
import { InputHandler } from '../../../../../viewer/input-handler';
import { SubscriptionTopic } from '../../../../../viewer/messaging/pubsub';
import { MeasureTool } from '../../../../../viewer/tools/measure-tool';
import { PoiTool } from '../../../../../viewer/tools/poi-tool';
import { SelectTool } from '../../../../../viewer/tools/select-tool';
import { AssetSnapSettings } from '../../../../../viewer/tools/utils/asset-snapping';
import { useFeatureFlag } from '../../../../hooks/use-feature-flag';
import { useShowAllHeights } from '../../../../hooks/use-show-all-heights';
import { useStoredSetting } from '../../../../hooks/use-stored-setting';
import { useSubscribeCallback } from '../../../../hooks/use-subscribe';
import { CheckBox } from '../../../check-box/check-box';
import { Dialog } from '../../../dialog/dialog';
import { Icon } from '../../../icon/icon';
import { LabelledContainer } from '../../../labelled-container/labelled-container';
import { SnapAssetsDialog } from '../../../skyview/right-menu/tools/design-tool-menu/dialogs/snap-assets-dialog';
import { MenuTooltip } from '../../../skyview/right-menu/tools/design-tool-menu/menu-tooltip';
import {
  NumberInput,
  SnapSetting,
} from '../../../skyview/right-menu/tools/design-tool-menu/sections/snapping-section';
import { SnapStyleButtons } from '../../../snap-style-buttons/snap-style-buttons';
import { Stack } from '../../../stack/stack';
import { Tooltip } from '../../../tooltip/tooltip';
import { getBesideAssetMenuPosition } from '../../asset-menu/dialogs/asset-dialog-position';
import { useFitsInWindow } from './use-fits-in-window';

/**
 * The tool whose snapping the dialog sets, and what identifies it: the active tool, or the
 * selected line or area whose points are moved. Undefined when there is nothing to snap.
 */
const getSnappingTarget = () => {
  const tool = InputHandler.instance.getSnappingTool();
  const key = InputHandler.instance.isSelectToolActive()
    ? AssetManager.instance.getSelected()?.uuid
    : tool?.name;

  return tool && isDefined(key) ? { tool, key } : undefined;
};

/**
 * The snapping of the measure tool, the POI tool and of moving the points of a line or an area,
 * with the flag on.
 */
export const SnappingDialog = () => {
  const enabled = useFeatureFlag('SK-7558-measure-snapping-options');
  return enabled ? <AutoOpeningSnappingDialog /> : null;
};

/**
 * Opens by itself beside the asset menu as a tool that it sets the snapping of is used, and closes
 * when it is no longer used. It can be closed, and opens again for another tool, or as another
 * line or area is selected.
 */
const AutoOpeningSnappingDialog = () => {
  // The tool and its key are kept together, so the dialog always shows the tool it changes.
  const [target, setTarget] = React.useState(getSnappingTarget);
  const [visible, setVisible] = React.useState(isDefined(target));

  const update = () => {
    const newTarget = getSnappingTarget();

    // Not when the tool is only set again, e.g. after a measurement is saved.
    if (newTarget?.key !== target?.key) {
      setTarget(newTarget);
      setVisible(isDefined(newTarget));
    }
  };

  useSubscribeCallback(SubscriptionTopic.SetTool, update);
  useSubscribeCallback(SubscriptionTopic.NewSelection, update);
  useSubscribeCallback(SubscriptionTopic.UnSelection, update);

  return visible && target ? (
    <SnappingDialogContent key={target.key} tool={target.tool} onClose={() => setVisible(false)} />
  ) : null;
};

const SnappingDialogContent = (props: {
  tool: MeasureTool | PoiTool | SelectTool;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const { snapping } = props.tool;
  const { settings, excludedSnapAssetIds } = React.useSyncExternalStore(
    snapping.subscribe,
    snapping.getSnapshot,
  );
  const tooltipInMenuEnabled = useFeatureFlag('SK-7540-tooltip-in-menu');
  const [tooltipInDialog, setTooltipInDialog] = useStoredSetting('snappingDialogTooltip');
  const [lightTooltip, setLightTooltip] = useStoredSetting('snappingDialogTooltipLight');
  const [allHeights, setAllHeights] = useStoredSetting('snappingDialogAllHeights');
  const [settingsShown, setSettingsShown] = useStoredSetting('snappingDialogSettingsShown');
  const allHeightsEnabled = useFeatureFlag('SK-7558-visualize-all-heights-option');
  useShowAllHeights(props.tool.heightsGuide, allHeightsEnabled && allHeights);
  const [collapsed, setCollapsed] = React.useState(false);
  const [showSnapAssetsDialog, setShowSnapAssetsDialog] = React.useState(false);
  const [position] = React.useState(() => getBesideAssetMenuPosition());
  const [tooltipElement, setTooltipElement] = React.useState<HTMLDivElement | null>(null);

  const onSettingsChanged = (newValue: Partial<AssetSnapSettings>) =>
    snapping.onSettingsChanged(newValue);

  return (
    <>
      <Dialog
        // Beside the asset menu, below the other dialogs there, e.g. the point list of the line or
        // area whose points are moved.
        autoDock={true}
        closeIcon={true}
        collapsible={true}
        // The options have it, so that the tooltip's band reaches the dialog's edges
        contentPadding={false}
        disableDimmer={true}
        // As high as the window has room for, see useFitsInWindow
        maxHeight={false}
        movable={true}
        position={position}
        width={280}
        onClose={props.onClose}
        onCollapsedChanged={setCollapsed}
      >
        {{
          header: t('section.snapping.title', { ns: 'skyviewDesign' }),
          content: (
            <FittingBody growing={tooltipElement}>
              <Options spacing={1.5}>
                <SnapStyleButtons
                  tooltipPlacement="right-start"
                  value={settings.styles}
                  onChange={(styles) => onSettingsChanged({ styles })}
                />

                <LabelledContainer text={t('section.snapping.snapTo', { ns: 'skyviewDesign' })}>
                  <Stack spacing={0.5}>
                    <CheckBox
                      checked={settings.pointCloud}
                      onChange={() => onSettingsChanged({ pointCloud: !settings.pointCloud })}
                    >
                      {t('snap.pointCloud', { ns: 'skyviewDesign' })}
                    </CheckBox>
                    <CheckBox
                      checked={settings.tiledModel}
                      onChange={() => onSettingsChanged({ tiledModel: !settings.tiledModel })}
                    >
                      {t('snap.tiledModel', { ns: 'skyviewDesign' })}
                    </CheckBox>
                    <Stack alignItems="center" direction="row" spacing={0.5}>
                      <CheckBox
                        checked={settings.assets}
                        onChange={() => onSettingsChanged({ assets: !settings.assets })}
                      >
                        {t('snap.assets', { ns: 'skyviewDesign' })}
                        {excludedSnapAssetIds.size > 0 && (
                          <CustomTag>
                            {t('section.snapping.snapAssets.custom', { ns: 'skyviewDesign' })}
                          </CustomTag>
                        )}
                      </CheckBox>
                      <Icon
                        aria-label={t('section.snapping.snapAssets.settings', {
                          ns: 'skyviewDesign',
                        })}
                        fixedWidth={true}
                        icon={['fal', 'cog']}
                        title={t('section.snapping.snapAssets.settings', { ns: 'skyviewDesign' })}
                        onClick={() => setShowSnapAssetsDialog(true)}
                        onHoverStyle={{ icon: ['fas', 'cog'] }}
                      />
                    </Stack>
                    <SnapSetting
                      choices={[
                        {
                          value: 'absolute',
                          title: t('section.snapping.height.absolute.text', {
                            ns: 'skyviewDesign',
                          }),
                          tooltip: t('section.snapping.height.absolute.tooltip', {
                            ns: 'skyviewDesign',
                          }),
                          content: (
                            <NumberInput
                              defaultValue={settings.absoluteHeight}
                              title={t('section.snapping.height.absolute.inputTitle', {
                                ns: 'skyviewDesign',
                              })}
                              onChange={(absoluteHeight) => onSettingsChanged({ absoluteHeight })}
                            />
                          ),
                        },
                        {
                          value: 'offset',
                          title: t('section.snapping.height.offset.text', { ns: 'skyviewDesign' }),
                          tooltip: t('section.snapping.height.offset.tooltip', {
                            ns: 'skyviewDesign',
                          }),
                          content: (
                            <NumberInput
                              defaultValue={settings.heightOffset}
                              title={t('section.snapping.height.offset.inputTitle', {
                                ns: 'skyviewDesign',
                              })}
                              onChange={(heightOffset) => onSettingsChanged({ heightOffset })}
                            />
                          ),
                        },
                      ]}
                      title={t('section.snapping.height.text', { ns: 'skyviewDesign' })}
                      value={settings.height}
                      onChange={(height) => onSettingsChanged({ height })}
                    />
                  </Stack>
                </LabelledContainer>

                {(tooltipInMenuEnabled || allHeightsEnabled) && (
                  <LabelledContainer
                    text={
                      <SettingsToggle
                        aria-expanded={settingsShown}
                        // Its own name: in the container's label, which would otherwise name it
                        aria-label={t('snappingDialog.settings', { ns: 'skyviewDesign' })}
                        type="button"
                        onClick={() => setSettingsShown(!settingsShown)}
                      >
                        {t('snappingDialog.settings', { ns: 'skyviewDesign' })}
                        <Icon fixedWidth={true} icon={[settingsShown ? 'fas' : 'fal', 'gear']} />
                      </SettingsToggle>
                    }
                  >
                    {settingsShown && (
                      <Stack spacing={0.4}>
                        {tooltipInMenuEnabled && (
                          <>
                            <Tooltip
                              content={t('snappingDialog.tooltipInDialog.tooltip.text', {
                                ns: 'skyviewDesign',
                              })}
                              mainAxisOffset={22}
                              placement="right"
                              title={t('snappingDialog.tooltipInDialog.tooltip.title', {
                                ns: 'skyviewDesign',
                              })}
                              wrapChildren={true}
                            >
                              <CheckBox
                                checked={tooltipInDialog}
                                onChange={() => setTooltipInDialog(!tooltipInDialog)}
                              >
                                {t('snappingDialog.tooltipInDialog.text', { ns: 'skyviewDesign' })}
                              </CheckBox>
                            </Tooltip>
                            <CheckBox
                              checked={lightTooltip}
                              disabled={!tooltipInDialog}
                              onChange={() => setLightTooltip(!lightTooltip)}
                            >
                              {t('section.options.tooltipInMenu.light', { ns: 'skyviewDesign' })}
                            </CheckBox>
                          </>
                        )}
                        {allHeightsEnabled && (
                          <Tooltip
                            content={t('section.options.allHeights.tooltip.text', {
                              ns: 'skyviewDesign',
                            })}
                            mainAxisOffset={22}
                            placement="right"
                            title={t('section.options.allHeights.tooltip.title', {
                              ns: 'skyviewDesign',
                            })}
                            wrapChildren={true}
                          >
                            <CheckBox
                              checked={allHeights}
                              onChange={() => setAllHeights(!allHeights)}
                            >
                              {t('section.options.allHeights.text', { ns: 'skyviewDesign' })}
                            </CheckBox>
                          </Tooltip>
                        )}
                      </Stack>
                    )}
                  </LabelledContainer>
                )}
              </Options>

              {/*
               * At the bottom, so that the options above don't move as it grows while measuring. Not
               * while collapsed, when it would be hidden: then it is next to the cursor.
               */}
              {tooltipInMenuEnabled && tooltipInDialog && !collapsed && (
                <Band ref={setTooltipElement}>
                  <DialogTooltip
                    inFlow={true}
                    light={lightTooltip}
                    title={t('menuTooltip.title', { ns: 'skyviewDesign' })}
                    tool={props.tool}
                  />
                </Band>
              )}
            </FittingBody>
          ),
        }}
      </Dialog>
      {showSnapAssetsDialog && (
        <SnapAssetsDialog tool={snapping} onClose={() => setShowSnapAssetsDialog(false)} />
      )}
    </>
  );
};

/**
 * The dialog's content, scrolling where the window it is in is too low for it, see
 * useFitsInWindow. Within the dialog, which tells it its window.
 */
const FittingBody = (props: { growing: HTMLElement | null; children: React.ReactNode }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  useFitsInWindow(ref, props.growing);
  return <Body ref={ref}>{props.children}</Body>;
};

// As high as the dialog where it fills a window of its own, so that the tooltip's band reaches its
// bottom there too
const Body = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  overflow-y: auto;
`;

const Band = styled.div`
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
`;

// The dialog's padding and their own, which the tooltip below them doesn't have
const Options = styled(Stack)`
  flex: none;
  padding: calc(var(--padding) + 0.5em);
`;

// The settings are hidden until they're needed, shown by the label or its cog
const SettingsToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4em;
  padding: 0;

  font: inherit;
  color: inherit;
  background: none;
  border: none;
  cursor: pointer;
`;

// A band across the bottom of the dialog, down to its bottom
const DialogTooltip = styled(MenuTooltip)`
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  margin: 0;

  > * {
    flex: 1 0 auto;
  }
`;

const CustomTag = styled.span`
  margin-left: 0.5em;
  color: ${(props) => props.theme.color.gray.dark};
  font-style: italic;
`;
